// Marca do Portal do Consumidor (#6117) — fonte única de nome, logo, contatos e textos que
// mudam entre a Vigor e uma empresa parceira (hoje só a Gran). Puro, sem React:
//   node --experimental-strip-types --test views/PortalConsumidor/marca/__tests__/marcaPortal.test.ts
//
// Quem decide QUAL marca o cliente logado vê é o banco (portal_consumidor_marca(), em
// supabase/migrations/20260927120000_portal_marca_parceira.sql); `decidirMarca` abaixo é o
// espelho da mesma regra, para teste e para quem precisar decidir no front.

import { EMAIL_SUPORTE, WHATSAPP_SUPORTE } from '../format.ts';

export type ChaveMarca = 'vigor' | 'gran';

export interface FaqItem { q: string; a: string }

export interface MarcaPortal {
  chave: ChaveMarca;
  /** Nome completo ("Vigor Energy", "Gran Energia"). */
  nome: string;
  /** Nome curto para frases ("com a Vigor", "da Gran"). */
  nomeCurto: string;
  /** Sufixo do título da aba do navegador. */
  tituloAba: string;
  /** Logo da tela de login e do cabeçalho. */
  logo: { src: string; alt: string };
  /** Ícone da aba do navegador. Sem ele, fica o do VigorHub (/favicon.png). */
  favicon?: string;
  /** Mostra o selo "Atendimento Vigor Energy" junto ao logo (parceira atendida pela Vigor). */
  seloAtendimentoVigor: boolean;
  /** Canais de atendimento. Por enquanto os da Vigor também para a Gran (decisão de
   *  27/09/2026: depois serão redirecionados para a Gran — trocar só aqui). */
  contato: { email: string; whatsapp: string; site: string };
  /** Frase de abertura da mensagem de WhatsApp. */
  saudacaoWhatsapp: string;
  /** Endereço da tela de login desta marca. */
  rotaLogin: string;
  /** Link do manual do consumidor (o manual existente é o da Vigor). */
  mostrarManual: boolean;
  /** Convite para aderir ao boleto único — não existe para quem já é 100% boleto único. */
  mostrarConviteBoletoUnico: boolean;
  /** Rótulos de economia: gráfico do Início, comparativo da fatura e frase da retenção. */
  economia: {
    graficoCom: string; graficoSem: string;
    comparativoCom: string; comparativoSem: string;
    frase: string; fraseVarias: (n: number) => string;
  };
  /** Cores do gráfico "com × sem" sobre o degradê da marca (linha "com" é sempre branca).
   *  `sem` precisa contrastar com o degradê e se distinguir do branco; `superficie` é o
   *  anel dos marcadores, da cor do fundo. */
  grafico: { sem: string; superficie: string };
  /** Card "Indicar" do Início (a indicação hoje leva para o site da Vigor). */
  mostrarIndicar: boolean;
  /** Regras do contrato da Vigor (aviso prévio de 90 dias) na tela "Antes de cancelar".
   *  Não valem para a parceira, cujo contrato é com ela e cujas regras não conhecemos. */
  mostrarRegrasContratoVigor: boolean;
  /** Subtítulo da tela "Minhas Faturas". */
  subtituloFaturas: string;
  /** Rótulo da parte da energia no "Entenda sua fatura" quando o boleto não é único. */
  rotuloBoleto: string;
  /** Tela de login. */
  login: { titulo: string; subtitulo: string; beneficios: { titulo: string; descricao: string }[]; rodape: string };
  faq: FaqItem[];
}

/** Canais da Vigor. As duas telas usam o WhatsApp da empresa, (62) 99118-3449 — o que o
 *  comunicado do Guilherme divulga. O portal da Vigor mostrava o número de uma funcionária
 *  até a #6289. */
const CONTATO_VIGOR_GRAN = { email: EMAIL_SUPORTE, whatsapp: WHATSAPP_SUPORTE, site: 'vigorenergy.com.br' };
const CONTATO_VIGOR_PORTAL = { email: EMAIL_SUPORTE, whatsapp: WHATSAPP_SUPORTE, site: 'vigorenergy.com.br' };

const FAQ_VIGOR: FaqItem[] = [
  {
    q: 'A energia muda fisicamente na minha casa?',
    a: 'Não. Você continua recebendo energia da mesma distribuidora, pela mesma rede elétrica. O que muda é a origem da energia (créditos gerados pelas usinas da Vigor) e o valor cobrado na fatura.',
  },
  {
    q: 'Existe fidelidade ou multa por cancelamento?',
    a: 'Quando há período de Fidelidade previsto no seu Plano de Benefícios, cancelar antes do fim desse prazo sem justa causa pode gerar multa de até 30% do valor médio mensal multiplicado pelos meses restantes. Fora da Fidelidade, ou durante a carência, a saída não gera multa, respeitado o aviso prévio de 90 dias.',
  },
  {
    q: 'O desconto é fixo todo mês?',
    a: 'Não. O Benefício Econômico é estimado e pode variar conforme o consumo, a disponibilidade de créditos das usinas, o reajuste tarifário da Equatorial e bandeiras tarifárias — não é uma garantia contratual de valor fixo.',
  },
  {
    q: 'Recebo duas faturas (distribuidora + Vigor)?',
    a: 'Sim. Você recebe a fatura normal da distribuidora (já com o desconto refletido na energia compensada) e a fatura da Vigor Energy referente à energia injetada.',
  },
];

// Só o que é certo para o cliente da Gran (decisão de 27/09/2026): fidelidade, multa e
// prazos dependem do contrato da Gran e ficam de fora até a Gran passar as regras dela.
const FAQ_GRAN: FaqItem[] = [
  {
    q: 'Continuo sendo cliente da Gran?',
    a: 'Sim. Seu vínculo, seu contrato e seus benefícios continuam com a Gran Energia. A Gran contratou a Vigor Energy para cuidar do atendimento, do suporte e da emissão e envio dos boletos.',
  },
  {
    q: 'Por que recebo um boleto só?',
    a: 'É o boleto único: numa cobrança só vão a conta da Equatorial e a energia da Gran com o seu desconto. Quando você paga o boleto, a conta da Equatorial é paga automaticamente. Não pague a fatura da Equatorial à parte, senão ela é paga duas vezes.',
  },
  {
    q: 'O que é cada parte do boleto?',
    a: 'Uma parte é a conta da Equatorial (a distribuidora, que continua entregando a energia pela mesma rede). A outra é a energia solar da Gran, já com o seu desconto. Em "Minhas faturas" você vê as duas partes de cada mês.',
  },
  {
    q: 'Com quem eu falo sobre o boleto ou tiro dúvidas?',
    a: 'Com o time da Vigor Energy, pelos canais desta página. Eles cuidam do seu atendimento e dos seus boletos em nome da Gran.',
  },
];

export const MARCAS: Record<ChaveMarca, MarcaPortal> = {
  vigor: {
    chave: 'vigor',
    nome: 'Vigor Energy',
    nomeCurto: 'Vigor',
    tituloAba: 'Vigor Energy',
    logo: { src: '/LOGO-01.png', alt: 'Vigor Energy' },
    seloAtendimentoVigor: false,
    contato: CONTATO_VIGOR_PORTAL,
    saudacaoWhatsapp: 'Olá! Sou cliente Vigor Energy e tenho uma dúvida (Portal do Consumidor).',
    rotaLogin: '/portal-consumidor',
    mostrarManual: true,
    mostrarConviteBoletoUnico: true,
    economia: {
      graficoCom: 'Com Vigor',
      graficoSem: 'Sem Vigor',
      comparativoCom: 'Com a Vigor',
      comparativoSem: 'Sem a Vigor',
      frase: 'Sua economia com a Vigor até aqui',
      fraseVarias: (n) => `Sua economia com a Vigor, somando suas ${n} unidades`,
    },
    grafico: { sem: '#F6A937', superficie: '#3A4522' },
    mostrarIndicar: true,
    mostrarRegrasContratoVigor: true,
    subtituloFaturas: 'Faturas da sua unidade consumidora e boletos Vigor.',
    rotuloBoleto: 'Boleto Vigor',
    login: {
      titulo: 'Portal do Consumidor\nVigor Energy',
      subtitulo: 'Acompanhe suas faturas, boletos e sua economia com energia limpa — tudo em um só lugar, quando você quiser.',
      beneficios: [
        { titulo: 'Suas faturas em um só lugar', descricao: 'Consulte o histórico e o status das faturas da sua unidade consumidora.' },
        { titulo: '2ª via e boletos', descricao: 'Acesse boletos e segunda via sem precisar entrar em contato.' },
        { titulo: 'Sua economia com energia limpa', descricao: 'Acompanhe quanto você economiza sendo consumidor Vigor Energy.' },
        { titulo: 'Atendimento direto', descricao: 'Fale com nosso time de atendimento quando precisar.' },
      ],
      rodape: 'Área exclusiva para consumidores Vigor Energy.',
    },
    faq: FAQ_VIGOR,
  },
  gran: {
    chave: 'gran',
    nome: 'Gran Energia',
    nomeCurto: 'Gran',
    tituloAba: 'Gran Energia',
    logo: { src: '/portal/gran/logo-gran.png', alt: 'Gran Energia' },
    favicon: '/portal/gran/favicon-gran.png',
    seloAtendimentoVigor: true,
    contato: CONTATO_VIGOR_GRAN,
    saudacaoWhatsapp: 'Olá! Sou cliente da Gran Energia e tenho uma dúvida (Portal do Consumidor).',
    rotaLogin: '/portal-consumidor/gran',
    mostrarManual: false,
    mostrarConviteBoletoUnico: false,
    economia: {
      graficoCom: 'Com desconto',
      graficoSem: 'Sem desconto',
      comparativoCom: 'Com desconto',
      comparativoSem: 'Sem desconto',
      frase: 'Sua economia até aqui',
      fraseVarias: (n) => `Sua economia, somando suas ${n} unidades`,
    },
    // Azul-claro: o amarelo da Vigor some no degradê laranja da Gran (contraste ≥ 3 — ver teste).
    grafico: { sem: '#A5F3FC', superficie: '#9A4414' },
    mostrarIndicar: false,
    mostrarRegrasContratoVigor: false,
    subtituloFaturas: 'Seu boleto único de cada mês: a conta da Equatorial e a energia da Gran numa cobrança só.',
    rotuloBoleto: 'Energia da Gran',
    login: {
      titulo: 'Portal do Consumidor\nGran Energia',
      subtitulo: 'Seus boletos, suas faturas e sua economia com energia solar da Gran — tudo em um só lugar.',
      beneficios: [
        { titulo: 'Seu boleto único', descricao: 'A conta da Equatorial e a energia da Gran numa cobrança só, com Pix e código de barras.' },
        { titulo: 'Suas faturas em um só lugar', descricao: 'Histórico de cada mês, o que foi pago e o que está a vencer.' },
        { titulo: 'Sua economia', descricao: 'Acompanhe quanto você economiza com a energia solar da Gran.' },
        { titulo: 'Atendimento Vigor Energy', descricao: 'Equipe dedicada e gerente de carteira cuidando do seu atendimento em nome da Gran.' },
      ],
      rodape: 'Área exclusiva para consumidores da Gran Energia.',
    },
    faq: FAQ_GRAN,
  },
};

/** Marca pelo slug que o banco devolve. Slug desconhecido (parceira nova sem textos no
 *  front ainda) cai na Vigor, nunca numa tela quebrada. */
export function marcaPorChave(chave: string | null | undefined): MarcaPortal {
  const k = String(chave || '').trim().toLowerCase();
  return (MARCAS as Record<string, MarcaPortal>)[k] ?? MARCAS.vigor;
}

/**
 * Espelho de portal_consumidor_marca(): recebe a marca de cada UC que o cliente enxerga
 * (null = UC da Vigor) e devolve a marca da tela. A parceira com mais UCs ganha se tiver
 * pelo menos tantas quanto a Vigor — empate fica com a parceira (decisão de 27/09/2026).
 */
export function decidirMarca(marcasDasUcs: (string | null | undefined)[]): string {
  let vigor = 0;
  const porParceira = new Map<string, number>();
  for (const m of marcasDasUcs) {
    if (!m) { vigor++; continue; }
    porParceira.set(m, (porParceira.get(m) || 0) + 1);
  }
  const [melhor] = Array.from(porParceira.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return melhor && melhor[1] >= vigor ? melhor[0] : 'vigor';
}

/** Slug da marca pela URL de login (…/portal-consumidor/gran). */
export function marcaDaRota(pathname: string): ChaveMarca | null {
  const m = /^\/portal-consumidor\/(gran)(?:\/|$)/.exec(pathname || '');
  return m ? (m[1] as ChaveMarca) : null;
}

/** Link de WhatsApp com a saudação da marca. */
export function linkWhatsapp(marca: MarcaPortal, texto?: string): string {
  return `https://wa.me/${marca.contato.whatsapp}?text=${encodeURIComponent(texto ?? marca.saudacaoWhatsapp)}`;
}

// ─── Comunicado da marca (#6117) ────────────────────────────────────────────────────────
// O comunicado (categoria 'comunicado' em avisos_consumidor, público da marca) NÃO passa
// pela fila de avisos com aceite: é informativo. Abre sozinho uma única vez — a primeira
// abertura já grava "visualizado" — e depois fica relido pelo cartão do Início.

export const CATEGORIA_COMUNICADO = 'comunicado';

export interface AvisoComunicado {
  id: string;
  categoria: string;
  data_inicio: string | null;
  data_fim: string | null;
}

/** Comunicado mais antigo em vigência que o cliente nunca abriu (sem nenhuma linha de
 *  confirmação, nem de "visualizado"). `null` = nada para abrir sozinho. */
export function comunicadoParaAbrir<T extends AvisoComunicado>(
  avisos: T[],
  confirmacoes: { aviso_id: string }[],
  agora: Date,
): T | null {
  const vistos = new Set(confirmacoes.map(c => c.aviso_id));
  const emVigor = (a: AvisoComunicado) =>
    (!a.data_inicio || new Date(a.data_inicio) <= agora) && (!a.data_fim || new Date(a.data_fim) >= agora);
  return avisos
    .filter(a => a.categoria === CATEGORIA_COMUNICADO && emVigor(a) && !vistos.has(a.id))
    .sort((a, b) => (a.data_inicio ? Date.parse(a.data_inicio) : 0) - (b.data_inicio ? Date.parse(b.data_inicio) : 0))[0] ?? null;
}
